<template>
  <Breadcrumb :items="breadcrumbs" />

  <div class="ds-page">
    <div class="ds-container">
      <div class="ds-page-header">
        <div>
          <h1 class="ds-page-header__title">إشعارات الطلاب</h1>
          <p class="ds-page-header__subtitle">إرسال إشعار داخل التطبيق لجميع الطلاب أو لطلاب سنة دراسية محددة</p>
        </div>
      </div>

      <div class="ds-card ds-card--form">
        <div class="form-field">
          <label class="field-label">العنوان</label>
          <input type="text" class="form-control" v-model="form.title" placeholder="عنوان الإشعار" maxlength="255" />
        </div>

        <div class="form-field mt-3">
          <label class="field-label">نص الإشعار</label>
          <textarea class="form-control" rows="4" v-model="form.message" placeholder="نص الإشعار" maxlength="2000"></textarea>
        </div>

        <div class="form-field mt-3">
          <label class="field-label">السنة الدراسية المستهدفة</label>
          <div class="d-flex gap-3 flex-wrap mb-2">
            <label class="target-option">
              <input type="radio" value="all" v-model="form.target_type" />
              <span>كل الطلاب</span>
            </label>
            <label class="target-option">
              <input type="radio" value="academic_year" v-model="form.target_type" />
              <span>سنة دراسية محددة</span>
            </label>
          </div>
          <select
            v-if="form.target_type === 'academic_year'"
            class="form-select"
            v-model="form.academic_year_id"
          >
            <option :value="null" disabled>اختر السنة الدراسية...</option>
            <option v-for="y in gradeStore.activeGrades" :key="y.id" :value="y.id">{{ y.name }}</option>
          </select>
        </div>

        <div class="form-field mt-3">
          <label class="target-option"><input type="checkbox" v-model="linkLesson" @change="resetLesson" /> ربط الإشعار بدرس محدد</label>
          <template v-if="linkLesson">
            <p class="text-muted mt-2">يصل إشعار الدرس فقط إلى الطلاب المسموح لهم بفتحه ضمن الجمهور المحدد.</p>
            <div class="d-flex gap-2 mb-2">
              <input class="form-control" v-model="lessonSearch" placeholder="ابحث باسم الدرس" aria-label="البحث عن درس" @keyup.enter="fetchLessons" />
              <button class="btn btn-outline-primary" :disabled="loadingLessons" @click="fetchLessons">بحث</button>
            </div>
            <select class="form-select" v-model="form.lesson_id" @change="form.content_id = null" aria-label="الدرس المستهدف">
              <option :value="null" disabled>{{ loadingLessons ? 'جاري تحميل الدروس...' : 'اختر الدرس...' }}</option>
              <option v-for="lesson in lessons" :key="lesson.id" :value="lesson.id">{{ lesson.parent_title }} / {{ lesson.title }} (#{{ lesson.id }})</option>
            </select>
            <p v-if="lessonError" role="alert" class="text-danger">{{ lessonError }}</p>
            <label v-if="selectedLesson" class="d-block mt-2">المحتوى الذي يفتح مباشرة
              <select class="form-select" v-model="form.content_id" aria-label="المحتوى المستهدف">
                <option :value="null">المحتوى الرئيسي للدرس</option>
                <option v-for="content in selectedLesson.contents" :key="content.id" :value="content.id">{{ content.title }}</option>
              </select>
            </label>
          </template>
        </div>

        <div class="mt-4 text-end">
          <button class="ds-btn ds-btn--primary" :disabled="!canSend || sending" @click="confirmSend">
            <i class="pi pi-spin pi-spinner" v-if="sending"></i>
            <i class="pi pi-send" v-else></i>
            {{ sending ? 'جارٍ الإرسال...' : 'إرسال' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import { useGradeStore } from '@/stores/useGradeStore.js';

function blankForm() {
  return { title: '', message: '', target_type: 'all', academic_year_id: null, lesson_id: null, content_id: null };
}

export default {
  name: 'StudentNotificationsIndex',
  components: { Breadcrumb },

  data() {
    return {
      breadcrumbs: [{ name: 'الرئيسية', path: '/' }, { name: 'إشعارات الطلاب', path: '/student-notifications' }],
      form: blankForm(),
      sending: false,
      linkLesson: false, lessons: [], lessonSearch: '', loadingLessons: false, lessonError: '',
    };
  },

  computed: {
    gradeStore() { return useGradeStore(); },

    selectedLesson() { return this.lessons.find(l => l.id === this.form.lesson_id); },

    canSend() {
      if (this.linkLesson && !this.form.lesson_id) return false;
      if (!this.form.title.trim() || !this.form.message.trim()) return false;
      if (this.form.target_type === 'academic_year' && !this.form.academic_year_id) return false;
      return true;
    },

    targetLabel() {
      if (this.form.target_type === 'all') return 'كل الطلاب';
      const year = this.gradeStore.activeGrades.find(y => y.id === this.form.academic_year_id);
      return year ? `طلاب: ${year.name}` : 'سنة دراسية محددة';
    },
  },

  watch: {
    'form.academic_year_id'() { if (this.linkLesson) this.resetLesson(); },
    'form.target_type'() { if (this.linkLesson) this.resetLesson(); },
  },
  methods: {
    resetLesson() {
      this.form.lesson_id = null;
      this.form.content_id = null;
      if (this.linkLesson) this.fetchLessons();
    },
    async fetchLessons() {
      this.loadingLessons = true;
      this.lessonError = '';
      try {
        const { data } = await axios.get('/admin/student-notifications/lessons', { params: {
          q: this.lessonSearch,
          academic_year_id: this.form.target_type === 'academic_year' ? this.form.academic_year_id : undefined,
        } });
        this.lessons = data.data;
        if (!this.lessons.some(l => l.id === this.form.lesson_id)) { this.form.lesson_id = null; this.form.content_id = null; }
      } catch { this.lessonError = 'تعذر تحميل الدروس. حاول البحث مرة أخرى.'; }
      finally { this.loadingLessons = false; }
    },
    async confirmSend() {
      if (!this.canSend) return;

      const { isConfirmed } = await Swal.fire({
        title: 'تأكيد إرسال الإشعار',
        html: `
          <p><strong>العنوان:</strong> ${this.escapeHtml(this.form.title)}</p>
          <p><strong>النص:</strong> ${this.escapeHtml(this.form.message)}</p>
          ${this.linkLesson ? '<p><strong>الدرس:</strong> ' + this.escapeHtml(this.selectedLesson?.title || '') + '</p>' : ''}
          <p><strong>المستهدفون:</strong> ${this.escapeHtml(this.targetLabel)}</p>
        `,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'إرسال',
        cancelButtonText: 'إلغاء',
      });
      if (!isConfirmed) return;

      this.sending = true;
      try {
        const { data } = await axios.post('/admin/student-notifications', {
          lesson_id: this.linkLesson ? this.form.lesson_id : null,
          content_id: this.linkLesson ? this.form.content_id : null,
          title: this.form.title,
          message: this.form.message,
          target_type: this.form.target_type,
          academic_year_id: this.form.target_type === 'academic_year' ? this.form.academic_year_id : null,
        });
        Swal.fire({ icon: 'success', title: data.message || 'تم الإرسال', timer: 2200, showConfirmButton: false });
        this.linkLesson = false;
        this.form = blankForm();
      } catch (e) {
        const msg = e?.response?.data?.message || Object.values(e?.response?.data?.errors || {})[0]?.[0] || 'حدث خطأ أثناء الإرسال';
        Swal.fire({ icon: 'error', title: msg, timer: 2500, showConfirmButton: false });
      } finally {
        this.sending = false;
      }
    },

    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    },
  },

  async mounted() {
    if (!this.gradeStore.activeGrades.length) await this.gradeStore.fetchActiveGrades();
  },
};
</script>

<style scoped>
.ds-page { background: var(--surface-ground, #f8fafc); color: var(--text-primary, #1e293b); }
.ds-card--form .form-select { background-color: var(--surface-card, #fff); color: var(--text-primary, #1e293b); }
.ds-card--form {
  background: var(--surface-card, #fff);
  border: 1px solid var(--surface-border, #e5e7eb);
  border-radius: 14px;
  padding: 1.5rem;
  max-width: 640px;
}
.target-option {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
  font-weight: 600;
}
</style>
